import React from 'react';
import { X, AlertTriangle } from 'lucide-react';
import Button from '../ui/Button';
import FocusedDialog from '../ui/FocusedDialog';

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  /** String stays a plain paragraph; pass a ReactNode for rich context bodies. */
  message: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info';
  icon?: React.ReactNode;
  /** Max-width preset. `md` is the default (plain-text dialogs). Use `lg` for
   *  rich content with lists / breakdowns so the body has room to breathe. */
  size?: 'md' | 'lg' | 'xl';
}

const WIDTH_CLS: Record<NonNullable<ConfirmDialogProps['size']>, string> = {
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
};

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'danger',
  icon,
  size = 'md',
}) => {
  if (!isOpen) return null;

  const getVariantStyles = () => {
    switch (variant) {
      case 'danger':
        return {
          iconColor: 'text-red-600',
          iconBg: 'bg-red-100 dark:bg-red-900/20',
          confirmButton: 'danger'
        };
      case 'warning':
        return {
          iconColor: 'text-yellow-600',
          iconBg: 'bg-yellow-100 dark:bg-yellow-900/20',
          confirmButton: 'warning'
        };
      case 'info':
        return {
          iconColor: 'text-blue-600',
          iconBg: 'bg-blue-100 dark:bg-blue-900/20',
          confirmButton: 'primary'
        };
      default:
        return {
          iconColor: 'text-red-600',
          iconBg: 'bg-red-100 dark:bg-red-900/20',
          confirmButton: 'danger'
        };
    }
  };

  const styles = getVariantStyles();

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  // Plain-string messages retain the original `<p>` typography. JSX bodies
  // get a neutral `<div>` wrapper so nested block elements remain valid HTML.
  const isPlainMessage = typeof message === 'string';

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <FocusedDialog
        ariaLabel={title}
        className={`bg-white dark:bg-gray-800 rounded-xl shadow-2xl ${WIDTH_CLS[size]} w-full animate-in fade-in duration-200 zoom-in-95`}
      >
        {/* Header */}
        <div className="flex items-start gap-4 p-6 pb-4">
          {/* Icon */}
          <div className={`flex-shrink-0 w-12 h-12 rounded-full ${styles.iconBg} flex items-center justify-center`}>
            {icon ? icon : <AlertTriangle className={`h-6 w-6 ${styles.iconColor}`} />}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              {title}
            </h3>
            {isPlainMessage ? (
              <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                {message}
              </p>
            ) : (
              <div className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                {message}
              </div>
            )}
          </div>

          {/* Close Button */}
          <button
            onClick={onClose}
            className="flex-shrink-0 w-8 h-8 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-center transition-colors"
            aria-label="Close dialog"
          >
            <X className="h-4 w-4 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3 px-6 pb-6">
          <Button
            variant="ghost"
            onClick={onClose}
            className="flex-1 whitespace-nowrap"
          >
            {cancelText}
          </Button>
          <Button
            variant={styles.confirmButton as any}
            onClick={handleConfirm}
            className="flex-1 whitespace-nowrap"
          >
            {confirmText}
          </Button>
        </div>
      </FocusedDialog>
    </div>
  );
};

export default ConfirmDialog;